import { TColumnName, TDataset, TPCA } from "../types";
import { TComputedValues } from "../reducers/root.slice";
import ColumnUtils from "./ColumnUtils";
import { COLOR_BAR, TRACE_CONFIG } from "../constants";
import { PlotData } from "plotly.js";
import MathUtils from "./MathUtils";
import RowUtils from "./RowUtils";
import Vector3, { TVector3 } from "../lib/Vector3";
import { mean, std, min, max } from "mathjs";
import VectorUtils from "../lib/Vector3";

type TTrace = {
  x: Array<string | number>;
  y: Array<string | number>;
  z: Array<string | number>;
  text: Array<string>;
};

type TProjection = {
  x?: { show: boolean; opacity: number };
  y?: { show: boolean; opacity: number };
  z?: { show: boolean; opacity: number };
};

class GraphUtils {
  static generateGraphData({
    rows,
    method,
    column,
    isContinuous,
    columnsForHover,
    computedValues,
    range,
    sdFilter,
    showProjectionX,
    showProjectionY,
    showProjectionZ,
    rotation,
    applyZScore,
    applyMinMaxScaling,
  }: {
    rows: TDataset;
    method: TColumnName;
    column: TColumnName;
    isContinuous: boolean;
    columnsForHover: TColumnName[];
    computedValues: TComputedValues;
    range?: [number, number];
    sdFilter: number;
    showProjectionX: boolean;
    showProjectionY: boolean;
    showProjectionZ: boolean;
    rotation: {
      x: number;
      y: number;
      z: number;
    };
    applyZScore: boolean;
    applyMinMaxScaling: boolean;
  }) {
    let farthestPoint = { x: 0, y: 0, z: 0 };
    let farthestPointDistance = 0;
    let zScoredValues;
    let minMaxScalingValues;

    if (applyZScore || applyMinMaxScaling) {
      const xArray = [];
      const yArray = [];
      const zArray = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const x_key = method;
        const { Y: y_key, Z: z_key } =
          ColumnUtils.getMethodColumnYZNames(method);
        const x = RowUtils.getValueAsNumber(row, x_key);
        const y = RowUtils.getValueAsNumber(row, y_key);
        const z = RowUtils.getValueAsNumber(row, z_key);

        xArray.push(x);
        yArray.push(y);
        zArray.push(z);
      }

      if (applyZScore) {
        const xMean = mean(xArray);
        const yMean = mean(yArray);
        const zMean = mean(zArray);
        const xStd = std(...xArray);
        const yStd = std(...yArray);
        const zStd = std(...zArray);

        zScoredValues = {
          xMean,
          yMean,
          zMean,
          xStd,
          yStd,
          zStd,
        };
      } else if (applyMinMaxScaling) {
        minMaxScalingValues = {
          xMin: min(xArray),
          yMin: min(yArray),
          zMin: min(zArray),
          xMax: max(xArray),
          yMax: max(yArray),
          zMax: max(zArray),
        };
      }
    }

    // sd range
    const sdMin = Math.max(
      computedValues.means[column] -
        sdFilter * computedValues.standardDeviations[column],
      computedValues.smallestValue[column]
    );
    const sdMax = Math.min(
      computedValues.means[column] +
        sdFilter * computedValues.standardDeviations[column],
      computedValues.largestValue[column]
    );

    const graphData: Record<
      string,
      TTrace & {
        value: Array<string | number>;
        row: Record<string, any>;
        tooltip: string[];
      }
    > = {};

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const value = RowUtils.getValue(row, column);

      if (!value) {
        console.warn(`Missing value for ${column}`);
        continue;
      }

      if (isContinuous) {
        const valueNumber = Number(value);

        if (valueNumber < sdMin || valueNumber > sdMax) {
          continue;
        }

        // histogram filter
        if (range && (valueNumber < range[0] || valueNumber > range[1])) {
          continue;
        }
      }

      // for continuous values all values goes in the same trace
      const traceKey = isContinuous ? "0" : value;

      if (!graphData[traceKey]) {
        graphData[traceKey] = {
          text: [],
          x: [],
          y: [],
          z: [],
          value: [],
          row,
          tooltip: [],
        };
      }

      const x_key = method;
      const { Y: y_key, Z: z_key } = ColumnUtils.getMethodColumnYZNames(method);
      const dr_method_x = RowUtils.getValueAsNumber(row, x_key);
      const dr_method_y = RowUtils.getValueAsNumber(row, y_key);
      const dr_method_z = RowUtils.getValueAsNumber(row, z_key);
      const subjectID = RowUtils.getId(row);

      if (!value || !subjectID) {
        console.warn("Missing value, subjectID, or x, y, or z for row:", row);
        continue;
      }

      const generateTooltip = () => {
        let tooltip = [];

        tooltip.push(`${column}: ${RowUtils.getValue(row, column)}`);

        tooltip = tooltip.concat(
          [...columnsForHover].map((column) => {
            return `${column}: ${RowUtils.getValue(row, column)}`;
          })
        );

        return tooltip.join("<br>");
      };

      graphData[traceKey].text.push(subjectID);

      let x = dr_method_x;
      let y = dr_method_y;
      let z = dr_method_z;

      if (zScoredValues) {
        x = (x - zScoredValues.xMean) / zScoredValues.xStd;
        y = (y - zScoredValues.yMean) / zScoredValues.yStd;
        z = (z - zScoredValues.zMean) / zScoredValues.zStd;
      } else if (minMaxScalingValues) {
        x =
          (x - minMaxScalingValues.xMin) /
          (minMaxScalingValues.xMax - minMaxScalingValues.xMin);
        y =
          (y - minMaxScalingValues.yMin) /
          (minMaxScalingValues.yMax - minMaxScalingValues.yMin);
        z =
          (z - minMaxScalingValues.zMin) /
          (minMaxScalingValues.zMax - minMaxScalingValues.zMin);
      }

      if (VectorUtils.distanceToOrigin({ x, y, z }) > farthestPointDistance) {
        farthestPoint = { x, y, z };
        farthestPointDistance = VectorUtils.distanceToOrigin({
          x,
          y,
          z,
        });
      }

      const result = Vector3.applyEuler(
        { x, y, z },
        {
          alpha: rotation.x / 57.2958,
          beta: rotation.y / 57.2958,
          gamma: rotation.z / 57.2958,
        }
      );

      graphData[traceKey].x.push(result.x);
      graphData[traceKey].y.push(result.y);
      graphData[traceKey].z.push(result.z);
      graphData[traceKey].value.push(value);
      graphData[traceKey].tooltip.push(generateTooltip());
    }

    const result = Object.values(graphData).map((datum, index) => {
      return GraphUtils.traceFromValue({
        ...datum,
        name: isContinuous ? column : String(datum.value[0]),
        color: isContinuous ? datum.value.map(Number) : undefined,
        showColorbar: isContinuous,
        showProjectionX,
        showProjectionY,
        showProjectionZ,
      });
    });

    return {
      graphData: result,
      sdMin,
      sdMax,
      farthestPoint,
      farthestPointDistance,
    };
  }

  static traceFromValue({
    x,
    y,
    z,
    text,
    color,
    tooltip,
    name,
    showColorbar,
    showProjectionX,
    showProjectionY,
    showProjectionZ,
  }: TTrace & {
    tooltip: string[];
    color?: string | string[] | number[];
    name: string;
    showColorbar: boolean;
    showProjectionX: boolean;
    showProjectionY: boolean;
    showProjectionZ: boolean;
  }) {
    let projection: TProjection | undefined = {};

    if (showProjectionX) {
      projection.x = {
        show: true,
        opacity: 0.5,
      };
    }

    if (showProjectionY) {
      projection.y = {
        show: true,
        opacity: 0.5,
      };
    }

    if (showProjectionZ) {
      projection.z = {
        show: true,
        opacity: 0.5,
      };
    }

    if (!showProjectionX && !showProjectionY && !showProjectionZ) {
      projection = undefined;
    }

    return {
      ...TRACE_CONFIG,
      marker: {
        ...TRACE_CONFIG.marker,
        color,
        colorbar: showColorbar ? COLOR_BAR : undefined,
      },
      text,
      x,
      y,
      z,
      hovertemplate: tooltip,
      name,
      projection,
    };
  }

  static generateDensityPlotData(graphData: Partial<PlotData>[]) {
    let x: any[] = [];
    let y: any[] = [];
    let z: any[] = [];

    graphData.forEach((graphDatum) => {
      x = x.concat(graphDatum.x);
      y = y.concat(graphDatum.y);
      z = z.concat(graphDatum.z);
    });

    return [
      {
        ncontours: 20,
        colorscale: "Hot" as const,
        reversescale: true,
        showscale: true,
        type: "histogram2dcontour" as const,
        x,
        y,
        z,
      },
    ];
  }

  static generateCategoricalHistogramData(graphData: Partial<PlotData>[]) {
    return graphData.map((datum) => ({
      x: [datum["text"]?.length ?? 0],
      name: datum.name,
      y: [""],
      orientation: "h" as const,
      type: "bar" as const,
      marker: {
        width: 1,
      },
    }));
  }

  static generateContinuousHistogramData({
    rows,
    selectedColumn,
    sdMin,
    sdMax,
    isConverted,
  }: {
    rows: TDataset;
    selectedColumn: TColumnName;
    sdMin: number;
    sdMax: number;
    isConverted: boolean;
  }) {
    const x: number[] = [];
    const noOfRows = rows.length;
    for (let i = 0; i < noOfRows; i++) {
      const value = RowUtils.getValue(rows[i], selectedColumn);

      if (!MathUtils.isNumberAsString(value)) {
        console.warn(
          "generateContinuousHistogramData: Missing value or not numeric for",
          selectedColumn
        );
        continue;
      }

      const valueAsNumber = Number(value);
      x[valueAsNumber] = x[valueAsNumber] ? x[valueAsNumber] + 1 : 1;
    }

    const traceSdXs: string[] = [];
    const traceSdYs: number[] = [];
    const traceRemainingXs: string[] = [];
    const traceRemainingYs: number[] = [];

    Object.keys(x).forEach((key) => {
      if (key === null) {
        return;
      }

      const keyAsNumber = Number(key);

      if (keyAsNumber < sdMin || keyAsNumber > sdMax) {
        traceRemainingXs.push(key);
        traceRemainingYs.push(x[keyAsNumber]);
      } else {
        traceSdXs.push(key);
        traceSdYs.push(x[keyAsNumber]);
      }
    });

    const type = isConverted ? ("bar" as const) : ("histogram" as const);

    return [
      {
        x: traceSdXs,
        y: traceSdYs,
        type,
      },
      {
        x: traceRemainingXs,
        y: traceRemainingYs,
        type,
        marker: {
          color: "black",
        },
      },
    ];
  }

  static addEigenVectors(
    graphData: Partial<PlotData>[],
    pca: TPCA,
    farthestPointDistance: number
  ) {
    // apply eigenvectors to graph data
    pca.eigenVectors.forEach((eigenVector, index) => {
      const color = index === 0 ? "red" : index === 1 ? "green" : "blue";
      const coordinate = index === 0 ? "x" : index === 1 ? "y" : "z";

      const lineTrace: Partial<PlotData> = {
        x: [0, eigenVector[0] * farthestPointDistance],
        y: [0, eigenVector[1] * farthestPointDistance],
        z: [0, eigenVector[2] * farthestPointDistance],
        type: "scatter3d",
        mode: "lines",
        name: "Eigen vector " + coordinate,
        showlegend: false,
        showscale: false,
        line: {
          color,
          width: 2,
        },
        marker: {
          width: 3,
        },
      };

      graphData.push(lineTrace as any);

      // cone for line
      const coneTrace = {
        x: [eigenVector[0] * farthestPointDistance],
        y: [eigenVector[1] * farthestPointDistance],
        z: [eigenVector[2] * farthestPointDistance],
        u: [eigenVector[0] * farthestPointDistance],
        v: [eigenVector[1] * farthestPointDistance],
        w: [eigenVector[2] * farthestPointDistance],
        type: "cone",
        name: "eigen_vector_cone_" + index,
        showscale: false,
        showlegend: false,
        line: {
          color,
          width: 2,
        },
        marker: {
          color,
        },
        sizemode: "absolute",
        sizeref: 0.2,
        colorscale: [
          ["0.0", color],
          ["1.0", color],
        ],
      };

      // text for the line
      const textTrace = {
        x: [eigenVector[0] * farthestPointDistance],
        y: [eigenVector[1] * farthestPointDistance],
        z: [eigenVector[2] * farthestPointDistance],
        mode: "text",
        name: `PCA${index + 1} text`,
        text: [`PCA${index + 1} = ${MathUtils.percentage(pca[coordinate])}%`],
        textposition: "bottom",
        type: "scatter3d",
        showlegend: false,
      };

      graphData.push(textTrace as any, coneTrace as any);
    });

    return graphData;
  }
}

export default GraphUtils;
